import { _ as _extends } from '../../_rollupPluginBabelHelpers-c245b26a.js';
import React, { forwardRef } from 'react';
import { Check } from '@indeed/ifl-icons';
import isFunction from '../../utils/isFunction.js';
import { useComboboxContext } from './useComboboxContext.js';
import { COMBOBOX_FILTERS, comboboxCollection } from './utils.js';
import { jsx } from '@emotion/react';
import { StyledMenuItem } from '../Menu/MenuItem.js';
import { Flex } from '../Flex/Flex.js';
import { Text } from '../Text/Text.js';
import '../Collection/Collection.js';
import '../../utils/mergeRefs.js';
import 'focus-visible';
import '../../reach-menu-button.esm-22bcdd5f.js';
import '../../reach-popover.esm-c19739dd.js';
import 'react-dom';
import '@indeed/ifl-css';
import '../../utils/styled.js';
import '../../utils/createResponsive.js';

/**
 * ComboboxMenuItem
 *
 * An option that is suggested to the user as they interact with the combobox.
 *
 * @deprecated The `Combobox` component will be removed in IFL 7.
 * See the IFL 7 preparation guide for more information: https://link.indeed.tech/ifl-7-prep-guide
 */

const ComboboxMenuItem = /*#__PURE__*/forwardRef((props, forwardedRef) => {
  const {
    children,
    disabled,
    selectIcon,
    sx,
    value,
    ...itemProps
  } = props;
  const {
    disabled: comboboxDisabled,
    filter,
    highlightedIndex,
    inputValue,
    items,
    setItems,
    getItemProps,
    selectedValue
  } = useComboboxContext();
  const {
    CollectionItem,
    useCollection
  } = comboboxCollection;
  const {
    getItems
  } = useCollection();
  React.useEffect(() => {
    setItems(getItems().map(item => item.value));
  }, [getItems, setItems]);
  if (filter !== null) {
    const filterToUse = filter || COMBOBOX_FILTERS.contains;
    const shouldRender = filterToUse(value, inputValue);
    if (!shouldRender) {
      return null;
    }
  }
  const findItemIndex = val => items.indexOf(val);
  const index = findItemIndex(value) > -1 ? findItemIndex(value) : 0;
  const isDisabled = comboboxDisabled || disabled || props["aria-disabled"] === "true";
  const isHighlighted = highlightedIndex === index;
  const isSelected = selectedValue === value;
  const hasIcon = selectIcon !== null;
  const icon = selectIcon !== undefined ? selectIcon : jsx(Check, {
    "aria-hidden": true
  });

  /**
   * Define Combobox-specific attributes and styles because there is currently
   * no way to target the "focus" state of the `StyledMenuItem`. Focus remains
   * on the input element, and thus, `[data-focus-visible]` is never added
   * within the "menu".
   */
  const itemStyles = {
    bg: "background.action",
    "&:hover": {
      bg: "background.action.hover"
    },
    "&:active": {
      bg: "background.action.active"
    },
    '&[data-combobox-item-has-focus="true"]': {
      bg: "background.action.weaker",
      boxShadow: "focus.inset"
    },
    '&[data-combobox-item-has-selection="true"]': {
      bg: "background.action.weaker",
      "&:hover": {
        bg: "background.action.hover"
      },
      "&:active": {
        bg: "background.action.active"
      }
    }
  };
  return jsx(CollectionItem, {
    value: value
  }, jsx(StyledMenuItem, _extends({
    as: "li",
    "data-combobox-item-has-selection": isSelected,
    "data-combobox-item-has-focus": isHighlighted,
    sx: {
      display: "flex",
      listStyle: "none",
      ...itemStyles,
      ...sx
    }
  }, getItemProps({
    ref: forwardedRef,
    /**
     * By default, Downshift will set `aria-selected` to "true" or "false"
     * depending on the highlighted item. `aria-selected="false"` will
     * announce "unselected" in some screen readers which isn't useful.
     * Instead, remove altogether.
     */
    "aria-selected": isSelected ? "true" : undefined,
    disabled: isDisabled,
    "aria-disabled": isDisabled,
    item: value,
    index,
    ...itemProps,
    onMouseMove: event => {
      /**
       * Hook into Downshift's default onMouseMove event.
       *
       * Prevent "highlighting" of item when moused over. `aria-selected`
       * and the input's `aria-activedescendant` attributes are only
       * updated via the arrow keys and explicit selection per the
       * WAI-ARIA Authoring Practices example.
       */

      // @ts-ignore
      event.nativeEvent.preventDownshiftDefault = true;
    }
  })), hasIcon && jsx(Flex, {
    sx: {
      marginInlineEnd: 3,
      opacity: isSelected ? "1" : "0",
      color: "foreground.weak",
      minInlineSize: "auto"
    }
  }, icon), jsx(Text, {
    variant: "contrast",
    sx: {
      flex: "1 1 auto"
    }
  }, isFunction(children) ? children({
    isSelected
  }) : children)));
});
ComboboxMenuItem.displayName = "ComboboxMenuItem";

export { ComboboxMenuItem };
